import {createGridDocument, promoteGridDocument, instanceIdSchema, patchBanner, validateGridDocument, type BannerInstance, type BannerPatch, type GridDocument} from "./document";
import * as s from "./schema";

export type GridCommand =
  | {type: "setTitle"; title: string}
  | {type: "addBanner"; banner: BannerInstance; index: number}
  | {type: "replaceBanner"; banner: BannerInstance}
  | {type: "updateBanner"; instanceId: string; patch: BannerPatch}
  | {type: "removeBanner"; instanceId: string}
  | {type: "duplicateBanner"; instanceId: string; newInstanceId: string}
  | {type: "reorderBanners"; instanceIds: string[]};

function exact(command: Record<string, unknown>, fields: string[]) {
  for (const key of Object.keys(command)) if (!fields.includes(key)) s.fail(`command.${key}`, "Unknown field");
  for (const key of fields) if (!Object.hasOwn(command, key)) s.fail(`command.${key}`, "Missing field");
}
function findBanner(document: GridDocument, instanceId: unknown): BannerInstance {
  const id = instanceIdSchema(instanceId);
  const banner = document.banners.find((entry) => entry.instanceId === id);
  if (!banner) throw new Error(`Unknown banner: ${id}`);
  return banner;
}

// Accept unknown at the runtime boundary, including future HTTP/agent callers.
export function applyGridCommand(document: GridDocument, input: unknown): GridDocument {
  validateGridDocument(document);
  return applyValidatedCommand(document, input);
}

// The boundary validates the input once. Each command validates the values it changes
// and preserves the remaining invariants, without decoding every other banner's SVG again.
function applyValidatedCommand(document: GridDocument, input: unknown): GridDocument {
  const command = s.object(input, "command");
  let next: GridDocument;
  switch (command.type) {
    case "setTitle":
      exact(command, ["type", "title"]);
      next = {...document, title: s.text(command.title, "command.title")};
      break;
    case "addBanner": {
      exact(command, ["type", "banner", "index"]);
      const index = s.range(0, document.banners.length, true)(command.index, "command.index");
      const single = {...createGridDocument(), schemaVersion: 2 as const, banners: [command.banner]};
      validateGridDocument(single);
      if (document.banners.some((banner) => banner.instanceId === single.banners[0].instanceId)) throw new Error(`Duplicate instance ID: ${single.banners[0].instanceId}`);
      next = {...document, banners: [...document.banners.slice(0, index), structuredClone(single.banners[0]), ...document.banners.slice(index)]};
      break;
    }
    case "replaceBanner": {
      exact(command, ["type", "banner"]);
      const single = {...createGridDocument(), schemaVersion: 2 as const, banners: [command.banner]};
      validateGridDocument(single);
      const replacement = single.banners[0];
      const original = findBanner(document, replacement.instanceId);
      next = {...document, banners: document.banners.map((banner) => banner === original ? structuredClone(replacement) : banner)};
      break;
    }
    case "updateBanner": {
      exact(command, ["type", "instanceId", "patch"]);
      const original = findBanner(document, command.instanceId);
      next = {...document, banners: document.banners.map((banner) => banner === original ? patchBanner(banner, command.patch as BannerPatch) : banner)};
      break;
    }
    case "removeBanner": {
      exact(command, ["type", "instanceId"]);
      const banner = findBanner(document, command.instanceId);
      next = {...document, banners: document.banners.filter((entry) => entry !== banner)};
      break;
    }
    case "duplicateBanner": {
      exact(command, ["type", "instanceId", "newInstanceId"]);
      const banner = findBanner(document, command.instanceId);
      const copy = {...structuredClone(banner), instanceId: instanceIdSchema(command.newInstanceId)};
      if (document.banners.some((entry) => entry.instanceId === copy.instanceId)) throw new Error(`Duplicate instance ID: ${copy.instanceId}`);
      const index = document.banners.indexOf(banner) + 1;
      next = {...document, banners: [...document.banners.slice(0, index), copy, ...document.banners.slice(index)]};
      break;
    }
    case "reorderBanners": {
      exact(command, ["type", "instanceIds"]);
      const ids = s.array(instanceIdSchema)(command.instanceIds, "command.instanceIds");
      if (ids.length !== document.banners.length || new Set(ids).size !== ids.length) throw new Error("Reorder requires every instance ID exactly once");
      next = {...document, banners: ids.map((id) => findBanner(document, id))};
      break;
    }
    default: return s.fail("command.type", "Unknown command");
  }
  return promoteGridDocument(next);
}

// No mutation or effects: a failed batch leaves the caller's original document intact.
export function applyGridCommands(document: GridDocument, commands: unknown): GridDocument {
  validateGridDocument(document);
  if (!Array.isArray(commands)) s.fail("commands", "Expected array");
  return commands.reduce((current, command) => applyValidatedCommand(current, command), document);
}
